// Выбор сущности с поиском. Один компонент на все разделы: и «привязать сделку к компании»,
// и «выбрать судно», и всё, что появится дальше.
//
// Почему не нативный <select>: сущностей десятки тысяч (74 тысячи сделок, 21 тысяча
// контактов) — список целиком не загрузить. Поэтому поиск по подстроке через ядро.
import { useEffect, useMemo, useRef, useState } from 'react'

import { useEntitySearch } from '../api/hooks'
import { ENTITIES } from '../lib/entities'
import type { EntityKind, EntityRef } from '../lib/entities'

interface Props {
  kind: EntityKind
  /** Что выбрано сейчас. null — ничего. */
  value: EntityRef | null
  onChange: (v: EntityRef | null) => void
  /** Подпись над полем. */
  label?: string
  placeholder?: string
  disabled?: boolean
  /** id для <label for>, чтобы поле было доступно с клавиатуры и для чтения с экрана. */
  id?: string
}

/** Задержка перед запросом: пользователь печатает, незачем дёргать ядро на каждую букву. */
const DEBOUNCE_MS = 250

export function EntityPicker({
  kind, value, onChange, label, placeholder, disabled, id,
}: Props) {
  const spec = ENTITIES[kind]
  const [text, setText] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)      // подсвеченная строка, для стрелок
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  // Клик вне поля закрывает список — иначе он висит поверх страницы.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const q = useEntitySearch(kind, debounced)
  const rows = useMemo(() => q.data ?? [], [q.data])

  useEffect(() => { setActive(0) }, [rows])

  const pick = (r: EntityRef) => {
    onChange(r)
    setText('')
    setDebounced('')
    setOpen(false)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (!open || !rows.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)) }
    if (e.key === 'Enter') { e.preventDefault(); pick(rows[active]) }
    if (e.key === 'Escape') setOpen(false)
  }

  // Уже выбрано — показываем выбранное с кнопкой сброса, поиск не нужен.
  if (value) {
    return (
      <div className="f picker">
        {label && <label>{label}</label>}
        <div className="picked">
          <span className="picked-title" title={value.title}>{value.title}</span>
          {value.hint && <span className="muted picked-hint">{value.hint}</span>}
          {!disabled && (
            <button type="button" className="picked-clear" onClick={() => onChange(null)}
                    aria-label="Сбросить выбор">×</button>
          )}
        </div>
      </div>
    )
  }

  const tooShort = debounced.trim().length > 0 && debounced.trim().length < spec.minQuery

  return (
    <div className="f picker" ref={boxRef}>
      {label && <label htmlFor={id}>{label}</label>}
      <input
        id={id}
        value={text}
        disabled={disabled}
        placeholder={placeholder ?? `Найти ${spec.one}…`}
        onChange={(e) => { setText(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={id ? `${id}-list` : undefined}
      />
      {open && (
        <div className="picker-drop" id={id ? `${id}-list` : undefined} role="listbox">
          {tooShort && <div className="picker-note muted">Введите хотя бы {spec.minQuery} символа</div>}
          {!tooShort && q.isFetching && <div className="picker-note muted">Ищем…</div>}
          {/* !! обязательно: тип ошибки у запроса — unknown, в JSX его подставлять нельзя */}
          {!tooShort && !q.isFetching && !!q.error && (
            <div className="picker-note err">Поиск недоступен: ядро не ответило</div>
          )}
          {!tooShort && !q.isFetching && !q.error && rows.length === 0 && !!debounced && (
            <div className="picker-note muted">Ничего не найдено</div>
          )}
          {rows.map((r, i) => (
            <button
              type="button"
              key={r.id}
              role="option"
              aria-selected={i === active}
              className={`picker-row${i === active ? ' active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(r)}
            >
              <span className="picker-row-title">{r.title}</span>
              {r.hint && <span className="muted picker-row-hint">{r.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
